import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Menu, MenuItem, Paper, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

// DetailPanelHeader presents the dealer/program identity and the top-level actions
// that should stay visible while the user works inside the selections tab.
const DetailPanelHeader = ({
  dealerName,
  bac,
  programLabel,
  lastUpdateDisplay,
  bacMrrDisplay,
  asMrrDisplay,
  isRefreshing,
  refreshDisabled,
  onRefresh,
  websiteLinks = [],
}) => {
  const [websiteAnchorEl, setWebsiteAnchorEl] = useState(null);
  const hasWebsiteLinks = websiteLinks.length > 0;
  const hasMultipleWebsites = websiteLinks.length > 1;

  const handleWebsiteClick = (event) => {
    if (!hasWebsiteLinks) return;
    // Open directly when there is a single website target; use a menu only when
    // the dealer has multiple brand-specific destinations.
    if (!hasMultipleWebsites) {
      const targetUrl = websiteLinks[0]?.url;
      if (targetUrl) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    setWebsiteAnchorEl(event.currentTarget);
  };

  const handleWebsiteClose = () => {
    setWebsiteAnchorEl(null);
  };

  const handleWebsiteSelect = (targetUrl) => {
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
    handleWebsiteClose();
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'flex-start' },
          gap: 2,
        }}
      >
        <Box sx={{ pr: 2, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: -0.3 }} noWrap>
            {dealerName}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Chip size="small" variant="outlined" color="primary" label={`BAC ${bac}`} />
            <Chip size="small" variant="outlined" label={programLabel || 'Program N/A'} />
          </Box>
          {lastUpdateDisplay ? (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                flexWrap: 'wrap',
                alignItems: 'stretch',
              }}
            >
              {lastUpdateDisplay ? (
                <Paper elevation={1} sx={{ p: 1.5, bgcolor: 'background.paper', minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary">
                    Last Update
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {lastUpdateDisplay}
                  </Typography>
                </Paper>
              ) : null}
              {bacMrrDisplay ? (
                <Paper elevation={1} sx={{ p: 1.5, bgcolor: 'background.paper', minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary">
                    BAC MRR
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {bacMrrDisplay}
                  </Typography>
                </Paper>
              ) : null}
              {asMrrDisplay ? (
                <Paper elevation={1} sx={{ p: 1.5, bgcolor: 'background.paper', minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary">
                    AS MRR
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {asMrrDisplay}
                  </Typography>
                </Paper>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1,
            flexWrap: { xs: 'initial', sm: 'wrap' },
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'stretch', md: 'flex-end' },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={onRefresh}
            disabled={refreshDisabled}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleWebsiteClick}
            disabled={!hasWebsiteLinks}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Website
          </Button>
          <Menu
            anchorEl={websiteAnchorEl}
            open={Boolean(websiteAnchorEl)}
            onClose={handleWebsiteClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {websiteLinks.map((link) => (
              <MenuItem key={link.label} onClick={() => handleWebsiteSelect(link.url)}>
                {link.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
    </Paper>
  );
};

export default DetailPanelHeader;
